import React, { useEffect, useMemo, useState, useCallback } from "react";
import moment from "moment-timezone";
import { Form } from "react-bootstrap";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";

import { useSite } from "../../util/sites";
import GYAxios from "../../util/GYAxios";
import LoaderGraph from "../general/LoaderGraph";

const PAGE_SIZE = 50;

const DATE_RANGES = {
	Today: [moment().startOf("day"), moment()],
	Yesterday: [moment().subtract(1, "days").startOf("day"), moment().subtract(1, "days").endOf("day")],
	"Last 7 Days": [moment().subtract(6, "days").startOf("day"), moment()],
	"Last 30 Days": [moment().subtract(29, "days").startOf("day"), moment()],
	"This Month": [moment().startOf("month"), moment()],
	"Last Month": [
		moment().subtract(1, "month").startOf("month"),
		moment().subtract(1, "month").endOf("month"),
	],
};

const formatDisplayDate = (date) => moment(date).format("MMMM DD, YYYY");

/** @param {{ logId: number }} props */
function InvLogRow({ logId }) {
	const [invLogs, setInvLogs] = useState(/** @type {Array<any>|null} */ (null));
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		setLoading(true);
		GYAxios.get(`/alarms/logs/inv/${logId}`)
			.then((res) => setInvLogs(res?.data?.data || []))
			.catch(() => setInvLogs([]))
			.finally(() => setLoading(false));
	}, [logId]);

	if (loading) {
		return (
			<tr>
				<td colSpan={7} style={{ background: "#f8f9fa", paddingLeft: "40px", fontStyle: "italic", color: "#888" }}>
					Loading inverter logs…
				</td>
			</tr>
		);
	}

	if (!invLogs || invLogs.length === 0) {
		return (
			<tr>
				<td colSpan={7} style={{ background: "#f8f9fa", paddingLeft: "40px", fontStyle: "italic", color: "#888" }}>
					There is no available inverter level log for this incidence.
				</td>
			</tr>
		);
	}

	return (
		<tr>
			<td colSpan={7} style={{ background: "#f8f9fa", padding: "0 0 0 40px" }}>
				<table className="table table-sm table-bordered mb-0" style={{ marginBottom: 0 }}>
					<thead style={{ background: "#e9ecef" }}>
						<tr>
							<th>Inverter</th>
							<th>Model State</th>
							<th>Start</th>
							<th>End</th>
							<th>Status</th>
						</tr>
					</thead>
					<tbody>
						{invLogs.map((inv) => {
							const isOpen = !inv.end_datetime;
							return (
								<tr key={inv.inv_log_id}>
									<td>INV {inv.inv_no}</td>
									<td>{inv.inv_state != null ?  inv.inv_state : "-"}</td>
									<td>{inv.start_datetime ? moment(inv.start_datetime).format("YYYY-MM-DD HH:mm:ss") : "-"}</td>
									<td>{isOpen ? "-" : moment(inv.end_datetime).format("YYYY-MM-DD HH:mm:ss")}</td>
									<td>
										<span
											style={{
												padding: "1px 6px",
												borderRadius: "4px",
												color: "#fff",
												fontSize: "0.8em",
												backgroundColor: isOpen ? "#dc3545" : "#28a745",
											}}
										>
											{isOpen ? "Open" : "Closed"}
										</span>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</td>
		</tr>
	);
}

function AlarmLogContent() {
	const currentSite = useSite();
	const [logs, setLogs] = useState([]);
	const [loading, setLoading] = useState(false);
	const [errorMsg, setErrorMsg] = useState("");
	const [page, setPage] = useState(1);
	const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
	const [dateRange, setDateRange] = useState({
		fromDate: moment().subtract(29, "days").format("YYYY-MM-DD"),
		toDate: moment().format("YYYY-MM-DD"),
	});
	const [expandedLogId, setExpandedLogId] = useState(null);

	const selectedSite = currentSite.selectedSite?.value;
	const selectedSiteId = selectedSite?.site_id;
	const selectedSiteName = selectedSite?.site_name || "";

	const fetchLogs = useCallback(() => {
		if (!selectedSiteId) return;

		setLoading(true);
		setErrorMsg("");
		const params = new URLSearchParams({
			page: String(page),
			limit: String(PAGE_SIZE),
			from: dateRange.fromDate,
			to: dateRange.toDate,
		});
		GYAxios.get(`/alarms/logs/site/${selectedSiteId}?${params.toString()}`)
			.then((res) => {
				const rows = res?.data?.data || [];
				setLogs(rows);
				setPagination(res?.data?.pagination || { total: 0, totalPages: 0 });
			})
			.catch((err) => {
				console.error(err);
				setErrorMsg("Cannot load alarm logs");
				setLogs([]);
				setPagination({ total: 0, totalPages: 0 });
			})
			.finally(() => setLoading(false));
	}, [selectedSiteId, page, dateRange]);

	// Reset page when site or date range changes
	useEffect(() => {
		setPage(1);
		setExpandedLogId(null);
	}, [selectedSiteId, dateRange]);

	// Fetch when site, page, or date range changes
	useEffect(() => {
		if (!selectedSiteId) {
			setLogs([]);
			setErrorMsg("");
			setPagination({ total: 0, totalPages: 0 });
			return;
		}
		fetchLogs();
	}, [selectedSiteId, page, dateRange, fetchLogs]);

	const handleDateRangeApply = useCallback((_event, picker) => {
		setDateRange({
			fromDate: picker.startDate.format("YYYY-MM-DD"),
			toDate: picker.endDate.format("YYYY-MM-DD"),
		});
	}, []);

	const tableRows = useMemo(
		() =>
			logs.map((row) => ({
				...row,
				start_datetime: row.start_datetime
					? moment(row.start_datetime).format("YYYY-MM-DD HH:mm:ss")
					: "-",
				end_datetime: row.end_datetime
					? moment(row.end_datetime).format("YYYY-MM-DD HH:mm:ss")
					: null,
				alarm_name: row.alarm_name || row.alarm_slug,
			})),
		[logs]
	);

	const startRow = (page - 1) * PAGE_SIZE + 1;

	if (!selectedSiteId) {
		return (
			<div style={{ height: "89vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
				<h3 style={{ color: "#b5b5b5" }}>Select a site to view alarm logs</h3>
			</div>
		);
	}

	return (
		<div className="container-fluid" style={{ paddingTop: "20px", paddingBottom: "20px" }}>
			<h3>Alarm Log - {selectedSiteName}</h3>

			{/* Filters */}
			<div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
				<div style={{ width: "100%", maxWidth: "600px" }}>
					<DateRangePicker
						onApply={handleDateRangeApply}
						initialSettings={{
							startDate: moment(dateRange.fromDate),
							endDate: moment(dateRange.toDate),
							opens: "center",
						}}
						ranges={DATE_RANGES}
						maxDate={moment()}
						alwaysShowCalendars
						autoApply
					>
						<Form>
							<Form.Group controlId="daterangepicker" style={{ marginBottom: 0 }}>
								<Form.Control
									type="text"
									placeholder="Select a date range"
									readOnly
									value={`${formatDisplayDate(dateRange.fromDate)} - ${formatDisplayDate(dateRange.toDate)}`}
									style={{ textAlign: "center" }}
								/>
							</Form.Group>
						</Form>
					</DateRangePicker>
				</div>
				{pagination.total > 0 && (
					<span style={{ color: "#888", fontSize: "0.9em" }}>
						{pagination.total} record{pagination.total !== 1 ? "s" : ""} found
					</span>
				)}
			</div>

			{errorMsg && <div className="alert alert-danger">{errorMsg}</div>}

			{loading ? (
				<LoaderGraph style={{ height: "50vh" }} />
			) : (
				<>
					<div className="table-responsive">
						<table className="table table-striped table-bordered table-hover">
							<thead>
								<tr>
									<th>#</th>
									<th>Alarm</th>
									<th>Alarm Code</th>
									<th>Start</th>
									<th>End</th>
									<th>Status</th>
									<th>Table</th>
									<th>Inv. Detail</th>
								</tr>
							</thead>
							<tbody>
								{tableRows.length > 0 ? (
									tableRows.map((row, idx) => {
										const isOpen = !row.end_datetime;
										const isExpanded = expandedLogId === row.log_id;
										return (
											<React.Fragment key={row.log_id || `${row.site_id}_${row.alarm_slug}_${idx}`}>
												<tr>
													<td>{startRow + idx}</td>
													<td>{row.alarm_name}</td>
													<td>{row.alarm_slug}</td>
													<td>{row.start_datetime}</td>
													<td>{isOpen ? "-" : row.end_datetime}</td>
													<td>
														<span
															style={{
																padding: "2px 8px",
																borderRadius: "4px",
																color: "#fff",
																fontSize: "0.85em",
																backgroundColor: isOpen ? "#dc3545" : "#28a745",
															}}
														>
															{isOpen ? "Open" : "Closed"}
														</span>
													</td>
													<td>{row.table_name}</td>
													<td style={{ textAlign: "center" }}>
														{row.alarm_slug === "INV PB" && (
															<button
																className="btn btn-sm btn-outline-info"
																style={{ fontSize: "0.8em", padding: "1px 8px" }}
																onClick={() => setExpandedLogId(isExpanded ? null : row.log_id)}
																title={isExpanded ? "Hide inverter logs" : "Show inverter logs"}
															>
																{isExpanded ? "▲ Hide" : "▼ Inverter Logs"}
															</button>
														)}
													</td>
												</tr>
												{isExpanded && <InvLogRow logId={row.log_id} />}
											</React.Fragment>
										);
									})
								) : (
									<tr>
										<td colSpan={8} style={{ textAlign: "center" }}>
											No alarm log for this site in the selected date range
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>

					{/* Pagination controls */}
					{pagination.totalPages > 1 && (
						<div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", marginTop: "12px" }}>
							<button
								className="btn btn-sm btn-outline-secondary"
								disabled={page <= 1}
								onClick={() => setPage(1)}
								title="First page"
							>
								«
							</button>
							<button
								className="btn btn-sm btn-outline-secondary"
								disabled={page <= 1}
								onClick={() => setPage((p) => Math.max(1, p - 1))}
							>
								‹ Prev
							</button>
							<span style={{ minWidth: "120px", textAlign: "center" }}>
								Page {page} of {pagination.totalPages}
							</span>
							<button
								className="btn btn-sm btn-outline-secondary"
								disabled={page >= pagination.totalPages}
								onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
							>
								Next ›
							</button>
							<button
								className="btn btn-sm btn-outline-secondary"
								disabled={page >= pagination.totalPages}
								onClick={() => setPage(pagination.totalPages)}
								title="Last page"
							>
								»
							</button>
						</div>
					)}
				</>
			)}
		</div>
	);
}

export default AlarmLogContent;
